import React, { useState } from 'react';
import axios from 'axios';
import { formatearFecha } from '../utils/fecha';
import { estadoDelPrestamo, siguienteCorte, conceptoDelAbono, historialPorMes } from '../utils/prestamo';
import { repartirCierre } from '../utils/cierre';
import { abonosFueraDePlazo, textoDelAviso as avisoDeFechas } from '../utils/revision';
import { estimadoPorDias } from '../utils/proporcional';
import { mensajeDeEstado, enlaceDeWhatsApp, fechaLarga,
         opcionesDeLosOtros } from '../utils/estadoDeCuenta';
import { imprimirRecibo } from '../utils/recibo';
import { buscarClientes } from '../utils/buscar';

const moneda = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

// Responde a la pregunta que hace el cliente: «¿cuánto debo?».
//
// Antes había que ir a Pagos, buscarlo, elegir cada préstamo uno por uno y
// sumar de cabeza. Aquí se busca por nombre o cédula y sale todo: capital,
// interés generado, lo abonado y el total, con el historial de cada abono.

// El historial de abonos, partido por meses.
//
// La pregunta que responde es la del cliente parado al frente: «¿y en qué se me
// fue la plata?». Ninguna fila puede contestarla sola —un abono de $400.000 se
// parte entre interés y capital, y a veces la mitad de un abono cubre lo que
// quedaba del interés—, pero el mes sí. Así que el reparto se dice donde es
// cierto: en un renglón encima de los abonos de cada mes.
//
// Los abonos que el motor descartó por tener fecha anterior al préstamo se
// quedan abajo, bajo su propio renglón, diciendo que no entran en la cuenta. La
// alternativa sería no mostrarlos, y esconder plata que alguien sí entregó es
// exactamente lo que no puede hacer este programa.
function FilasDelHistorial({ prestamo, estado, pagos, cliente }) {
  const { bloques, porAbono } = historialPorMes(prestamo, estado);

  const filaDeAbono = (p) => (
    <tr key={p.id}>
      <td style={{ whiteSpace: 'nowrap' }}>{formatearFecha(p.payment_date)}</td>
      <td><strong>{moneda(p.amount)}</strong></td>
      <td style={{ fontSize: '13px' }}>
        <span style={{ fontWeight: 'bold', color: conceptoDelAbono(prestamo, p).color }}>
          {conceptoDelAbono(prestamo, p).texto}
        </span>
        {/* Se distingue lo reconstruido de lo anotado el día que ocurrió: ante
            una discusión no es lo mismo. */}
        {p.generado === 1 && (
          <div style={{ color: '#9ca3af', fontSize: '11px' }}>cargado del historial</div>
        )}
      </td>
      <td style={{ textAlign: 'right' }}>
        {/* Reimprimir el recibo de cualquier abono, no solo del último: el que
            se pierde es siempre uno de hace meses. */}
        <button
          onClick={() => imprimirRecibo({ cliente, prestamo, pago: p, pagos })}
          title="Imprimir el recibo de este abono"
          style={{ background: 'none', border: 'none', cursor: 'pointer',
                   fontSize: '15px', padding: '4px' }}
        >
          🧾
        </button>
      </td>
    </tr>
  );

  // Cuota fija y modelo antiguo no tienen reparto por mes que enseñar.
  if (bloques.length === 0) return <>{pagos.map(filaDeAbono)}</>;

  const renglonDeMes = (bloque, clave) => {
    if (!bloque) {
      return (
        <tr key={clave} style={{ background: '#fffbeb' }}>
          <td colSpan={4} style={{ padding: '9px 12px', borderTop: '2px solid #fde68a',
                                   fontSize: '12px', color: '#92400e', fontWeight: 'bold' }}>
            Estos abonos no entran en la cuenta: su fecha es anterior al préstamo
          </td>
        </tr>
      );
    }
    const enCurso = !bloque.cerrado;
    return (
      <tr key={clave} style={{ background: enCurso ? '#eff6ff' : '#f8fafc' }}>
        <td colSpan={4} style={{ padding: '9px 12px',
                                 borderTop: `2px solid ${enCurso ? '#bfdbfe' : '#e2e8f0'}` }}>
          <div style={{ fontWeight: 'bold', fontSize: '13px',
                        color: enCurso ? '#1e40af' : '#334155' }}>
            {enCurso
              ? `Este mes, cierra el ${fechaLarga(bloque.corte)} · lleva abonado ${moneda(bloque.abonado)}`
              : `Mes cerrado el ${fechaLarga(bloque.corte)} · abonó ${moneda(bloque.abonado)}`}
            {/* El corte se guarda como medianoche UNIVERSAL. Con el formateador
                de siempre —que lee la hora local— un corte del 13 se muestra
                como 12 en Colombia. `fechaLarga` lee las partes universales,
                que es justo para lo que existe. */}
          </div>
          <div style={{ fontSize: '12px', marginTop: '2px',
                        color: enCurso ? '#1d4ed8' : '#475569' }}>
            {/* En el mes en curso esto es una proyección, no un hecho: si abona
                otra vez antes del corte cambia. Se dice, para que nadie lo lea
                como definitivo y después reclame. */}
            {enCurso && 'Si cerrara hoy: '}
            {moneda(bloque.aInteres)} al interés
            {' · '}
            {moneda(bloque.aCapital)} a la deuda
            {!enCurso && bloque.quedaDebiendoInteres > 0 &&
              ` · quedó debiendo ${moneda(bloque.quedaDebiendoInteres)} de interés`}
          </div>
        </td>
      </tr>
    );
  };

  const filas = [];
  let anterior;
  let primera = true;
  for (const p of pagos) {
    const bloque = porAbono.get(p.id);
    if (primera || bloque !== anterior) filas.push(renglonDeMes(bloque, `mes-${p.id}`));
    anterior = bloque;
    primera = false;
    filas.push(filaDeAbono(p));
  }
  return <>{filas}</>;
}

function Bloque({ etiqueta, valor, color, pie }) {
  return (
    <div>
      <div style={{ color: '#6b7280', fontSize: '12px' }}>{etiqueta}</div>
      <strong style={{ color: color || '#1f2937', fontSize: '17px' }}>{valor}</strong>
      {pie && <div style={{ color: '#9ca3af', fontSize: '11px' }}>{pie}</div>}
    </div>
  );
}

const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun',
                      'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
// En universal, como el resto de las fechas del programa: la de un corte se
// genera a medianoche universal y leerla en hora local la corre un día.
const fechaCorta = (iso) => {
  const d = new Date(String(iso).length <= 10 ? iso + 'T00:00:00Z' : iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getUTCDate()} ${MESES_CORTOS[d.getUTCMonth()]}`;
};

function DeudaCliente({ clients, onRefresh, ajustes }) {
  const [busqueda, setBusqueda] = useState('');
  // Préstamo que se está cerrando, con el monto que se recibe.
  const [cerrando, setCerrando] = useState(null);
  const [recibido, setRecibido] = useState('');
  const [guardando, setGuardando] = useState(false);
  // Préstamo al que se le va a rellenar el historial, y hasta qué fecha.
  const [rellenando, setRellenando] = useState(null);
  const [hastaFecha, setHastaFecha] = useState('');
  const [cliente, setCliente] = useState(null);
  const [prestamos, setPrestamos] = useState([]);
  // Se cierra al cambiar de cliente: si no, queda abierto el del anterior.
  const [viendoMensaje, setViendoMensaje] = useState(false);
  const [pagosPorPrestamo, setPagosPorPrestamo] = useState({});
  const [cargando, setCargando] = useState(false);

  const coincidencias = cliente ? [] : buscarClientes(clients, busqueda, 8);

  const elegir = async (c) => {
    setCliente(c);
    setBusqueda(c.name);
    setViendoMensaje(false);
    setCargando(true);
    try {
      const { data } = await axios.get(`/api/loans/${c.id}?incluir=todos`);
      const lista = data || [];
      setPrestamos(lista);

      const pares = await Promise.all(
        lista.map(l =>
          axios.get(`/api/payments/${l.id}`)
            .then(r => [l.id, Array.isArray(r.data) ? r.data : []])
            .catch(() => [l.id, []])
        )
      );
      setPagosPorPrestamo(Object.fromEntries(pares));
    } catch (err) {
      console.error('Error cargando la deuda:', err);
    }
    setCargando(false);
  };

  const limpiar = () => {
    setCliente(null);
    setBusqueda('');
    setPrestamos([]);
    setPagosPorPrestamo({});
  };

  const abrirRelleno = (prestamo) => {
    setRellenando(prestamo);
    setHastaFecha(new Date().toISOString().split('T')[0]);
  };

  // Cuántos cobros caben entre el inicio y la fecha elegida, para poder
  // enseñar lo que se va a crear antes de crearlo.
  const cobrosAGenerar = (prestamo, hastaTexto) => {
    const inicio = new Date(prestamo.start_date);
    const hasta = hastaTexto ? new Date(`${hastaTexto}T23:59:59`) : new Date();
    if (isNaN(inicio.getTime()) || isNaN(hasta.getTime()) || hasta < inicio) return 0;

    const hoy = new Date();
    const limite = hasta > hoy ? hoy : hasta;
    const dias = { daily: 1, weekly: 7, biweekly: 15, monthly: 30 };

    let n = 0;
    for (let i = 1; i <= 5000; i++) {
      const corte = new Date(inicio);
      if (prestamo.frequency === 'monthly' || !dias[prestamo.frequency]) {
        corte.setMonth(corte.getMonth() + i);
      } else {
        corte.setDate(corte.getDate() + dias[prestamo.frequency] * i);
      }
      if (corte > limite) break;
      n++;
    }
    return n;
  };

  const confirmarRelleno = async () => {
    setGuardando(true);
    try {
      await axios.post(`/api/loans/${rellenando.id}/generar-historial`, { hasta: hastaFecha });
      setRellenando(null);
      await elegir(cliente);
      if (onRefresh) onRefresh();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || err.message));
    }
    setGuardando(false);
  };

  const abrirCierre = (prestamo, estado) => {
    setCerrando({ prestamo, estado });
    setRecibido(String(Math.round(estado.deudaTotal)));
  };

  // Reparto: primero el interés, después el capital. Es lo habitual y lo que
  // conviene a quien presta, porque el interés sin pagar sigue acumulándose.
  // El reparto vive en utils/cierre.js, con sus pruebas: es plata, y una
  // cuenta de plata metida dentro de una pantalla no se puede probar.

  const confirmarCierre = async () => {
    const monto = parseFloat(recibido);
    if (!Number.isFinite(monto) || monto < 0) {
      alert('El monto debe ser un número que no sea negativo');
      return;
    }

    const { estado, prestamo } = cerrando;
    // Se registra TODO lo que entró. Antes se topaba con
    // `Math.min(monto, deudaTotal)` y la diferencia se perdía sin que nada lo
    // dijera: a Marco Meléndez se le prestó a los cinco días y pagó el mes
    // completo, y esos $200.000 de ganancia quedaban sin registrar.
    const r = repartirCierre(monto, estado);
    if (!r) return;

    if (r.deMas > 0 && !window.confirm(
      `Recibió ${moneda(r.recibido)} y el programa tenía calculada una deuda de ` +
      `${moneda(estado.deudaTotal)}.\n\n` +
      `Los ${moneda(r.deMas)} de más se van a registrar como INTERÉS — que es lo ` +
      `que son cuando se cobra el mes completo y el cliente devuelve antes.\n\n` +
      `¿Está bien?`
    )) return;

    setGuardando(true);
    try {
      await axios.post(`/api/loans/${prestamo.id}/cerrar`, {
        recibido: r.recibido, interes: r.interes,
        capital: r.capital, condonado: r.condonado
      });
      setCerrando(null);
      await elegir(cliente);
      if (onRefresh) onRefresh();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || err.message));
    }
    setGuardando(false);
  };

  const estados = prestamos.map(p => ({
    prestamo: p,
    estado: estadoDelPrestamo(p, pagosPorPrestamo[p.id] || []),
    pagos: (pagosPorPrestamo[p.id] || [])
      .slice()
      .sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date))
  }));

  const activos = estados.filter(e => e.prestamo.status === 'active');

  // Para mandarle la cuenta se toma el préstamo con más deuda. Casi siempre
  // tiene uno solo; si tiene varios, ese es el que motivó la llamada.
  //
  // A diferencia de Estados de cuenta —que es la ronda del día del corte—, aquí
  // se puede mandar en cualquier fecha, porque es para cuando el cliente
  // pregunta. El mensaje avisa de lo abonado que aún no se ha repartido, así
  // que a mitad de mes también dice la verdad.
  const principal = activos.slice()
    .sort((a, b) => b.estado.deudaTotal - a.estado.deudaTotal)[0];

  // El desglose tiene que sumar exactamente lo de arriba, o no sirve de nada.
  //
  // `interesPendiente` ya viene con lo abonado después del corte descontado, y
  // eso lo dejaba en negativo y la fila del interés en cero: el cliente veía un
  // capital enorme, un interés en cero y un total que no salía de sumar
  // ninguno de los dos. Se separan las dos cosas —el interés que se debe y lo
  // que entregó y todavía no se reparte— para que la resta se vea:
  //
  //   capital + interés que debe − lo que ya entregó = debe hoy
  const totales = activos.reduce((t, e) => {
    const espera = e.estado.enEspera || 0;
    return {
      capital: t.capital + e.estado.capitalPendiente,
      interes: t.interes + Math.max(0, e.estado.interesPendiente + espera),
      espera: t.espera + espera,
      // Todo lo que ha entregado, incluido lo que aún no se reparte. Sin
      // sumarlo, al que abonó ayer le salía «ya abonado $0» al lado de su
      // propio abono, que es la manera más rápida de perder la confianza en
      // una pantalla de plata.
      abonado: t.abonado + e.estado.interesPagado + e.estado.abonadoACapital + espera,
      deuda: t.deuda + e.estado.deudaTotal
    };
  }, { capital: 0, interes: 0, espera: 0, abonado: 0, deuda: 0 });

  // EL MENSAJE VA AQUÍ ABAJO, después de `totales`, y no es un detalle de orden.
  //
  // Esta pantalla armaba el mensaje sin decirle que el cliente tenía otro
  // préstamo ni pasarle el total, teniéndolo en letra grande dos renglones más
  // arriba. Al Dr. Suárez le salía «Debe hoy: $3.156.870» desde este botón
  // mientras la tarjeta de encima decía $4.517.851.
  //
  // `opcionesDeLosOtros` es la misma función que usa Estados de cuenta: dos
  // maneras de armar esto son dos mensajes distintos para el mismo cliente
  // según por dónde se le mande, que es justo lo que pasó.
  const mensaje = (cliente && principal)
    ? mensajeDeEstado(cliente, principal.prestamo, principal.pagos, {
        saludo: (ajustes && ajustes.saludo) || 'Buenas',
        cierre: (ajustes && ajustes.cierre) || 'Cualquier duda me escribe.',
        proximoCorte: siguienteCorte(principal.prestamo, new Date()),
        ...opcionesDeLosOtros(activos, principal),
      })
    : null;
  const urlWhatsApp = mensaje ? enlaceDeWhatsApp(cliente.phone, mensaje) : null;

  // En qué va a quedar cuando cierre el mes que está corriendo.
  //
  // Sin esto la pantalla y el estado de cuenta se contradecían: el mensaje le
  // decía al cliente en cuánto queda en el corte, y aquí salía otra cifra —la
  // de liquidar hoy, sin el interés del mes, que todavía no se ha cobrado—.
  // Salen las dos, cada una con su nombre.
  const proyecciones = activos.map(e => e.estado.proyeccion).filter(Boolean);
  const enElCorte = proyecciones.reduce((t, p) => t + p.saldoFinal, 0);
  const fechasDeCorte = [...new Set(proyecciones
    .map(p => (p.corte ? new Date(p.corte).toISOString().slice(0, 10) : null))
    .filter(Boolean))];
  const mostrarCorte = proyecciones.length > 0 &&
    Math.round(enElCorte) !== Math.round(totales.deuda);

  return (
    <div className="deuda-page">
      <h2>🧮 Deuda del cliente</h2>
      <p style={{ color: '#6b7280', marginTop: 0 }}>
        Busca por nombre o cédula y verás cuánto debe hoy, con el detalle de
        cada abono.
      </p>

      <div className="form-card">
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>🔍 Nombre o cédula</label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={busqueda}
              placeholder="Ej: Juan López o 1234567890"
              onChange={(e) => { setBusqueda(e.target.value); setCliente(null); }}
            />

            {coincidencias.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, background: 'white',
                border: '1px solid #e5e7eb', borderRadius: '6px', marginTop: '5px',
                maxHeight: '240px', overflowY: 'auto', zIndex: 10,
                boxShadow: '0 4px 6px rgba(0,0,0,.1)'
              }}>
                {coincidencias.map(c => (
                  <div
                    key={c.id}
                    onClick={() => elegir(c)}
                    style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                  >
                    <strong>{c.name}</strong>
                    {c.cedula && <span style={{ color: '#6b7280', marginLeft: '10px' }}>({c.cedula})</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          {cliente && (
            <button
              type="button"
              onClick={limpiar}
              style={{
                marginTop: '10px', padding: '7px 14px', borderRadius: '6px',
                border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', fontSize: '13px'
              }}
            >
              Buscar otro
            </button>
          )}
        </div>
      </div>

      {cargando && <p>Cargando...</p>}

      {cliente && !cargando && prestamos.length === 0 && (
        <div className="success-card">
          <h3>✅ {cliente.name} no debe nada</h3>
          <p>No tiene préstamos activos.</p>
        </div>
      )}

      {cliente && !cargando && prestamos.length > 0 && (
        <>
          {/* El total va arriba: es lo que el cliente pregunta. */}
          <div style={{
            background: 'white', border: '2px solid #667eea', borderRadius: '12px',
            padding: '22px', marginBottom: '20px', boxShadow: '0 4px 12px rgba(102,126,234,.15)'
          }}>
            <div style={{ color: '#6b7280', fontSize: '14px' }}>
              {totales.deuda > 0
                ? `${cliente.name} debe hoy`
                : `${cliente.name} no debe nada`}
            </div>
            <div style={{ fontSize: '38px', fontWeight: 'bold', color: '#4338ca', lineHeight: 1.2 }}>
              {moneda(totales.deuda)}
            </div>
            {mostrarCorte && (
              <div style={{ fontSize: '13px', color: '#4b5563', marginTop: '4px' }}>
                {fechasDeCorte.length === 1
                  ? `Al cerrar el mes el ${fechaCorta(fechasDeCorte[0])} queda en `
                  : 'Al cerrar sus meses queda en '}
                <strong>{moneda(enElCorte)}</strong>
                <span style={{ color: '#9ca3af' }}> · ya con el interés del mes</span>
              </div>
            )}
            <div style={{
              display: 'flex', gap: '26px', marginTop: '16px', paddingTop: '14px',
              borderTop: '1px solid #e5e7eb', flexWrap: 'wrap'
            }}>
              <Bloque etiqueta="Capital" valor={moneda(totales.capital)} color="#991b1b" />
              <Bloque etiqueta="Interés pendiente" valor={moneda(totales.interes)} color="#b45309" />
              {totales.espera > 0 && (
                <Bloque
                  etiqueta="De eso, sin repartir"
                  valor={'−' + moneda(totales.espera)}
                  color="#15803d"
                  pie="se reparte en el corte"
                />
              )}
              <Bloque etiqueta="Ya abonado" valor={moneda(totales.abonado)} color="#166534" />
              {/* Solo los abiertos: los cerrados siguen en la lista de abajo
                  como historial, pero contarlos aquí haría creer que se debe
                  algo por ellos. */}
              <Bloque
                etiqueta="Préstamos"
                valor={activos.length}
                pie={activos.length === 1 ? 'abierto' : 'abiertos'}
              />
            </div>

            {/* Mandarle su cuenta ahí mismo: quien mira esta pantalla es porque
                el cliente está preguntando, y leerle las cifras por teléfono es
                justo donde se equivoca uno. */}
            {mensaje && (
              <div style={{ marginTop: '16px', paddingTop: '14px',
                            borderTop: '1px solid #e5e7eb', display: 'flex',
                            gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                {urlWhatsApp ? (
                  <a href={urlWhatsApp} target="_blank" rel="noopener noreferrer"
                     style={{ padding: '9px 16px', borderRadius: '6px',
                              background: '#25D366', color: 'white', fontWeight: 700,
                              fontSize: '14px', textDecoration: 'none' }}>
                    💬 Mandarle su cuenta
                  </a>
                ) : (
                  <span style={{ padding: '9px 16px', borderRadius: '6px',
                                 background: '#f3f4f6', color: '#9ca3af', fontSize: '14px' }}>
                    Sin teléfono registrado
                  </span>
                )}
                <button onClick={() => setViendoMensaje(!viendoMensaje)}
                        style={{ padding: '9px 16px', borderRadius: '6px',
                                 border: '1px solid #d1d5db', background: 'white',
                                 cursor: 'pointer', fontSize: '14px' }}>
                  {viendoMensaje ? 'Ocultar' : 'Ver el mensaje'}
                </button>
              </div>
            )}

            {viendoMensaje && mensaje && (
              <pre style={{ marginTop: '12px', padding: '14px', background: '#f9fafb',
                            border: '1px solid #e5e7eb', borderRadius: '8px',
                            whiteSpace: 'pre-wrap', fontSize: '13px', lineHeight: 1.5,
                            fontFamily: 'inherit' }}>
                {mensaje}
              </pre>
            )}
          </div>

          {estados.map(({ prestamo, estado, pagos }, i) => (
            <div className="table-container" key={prestamo.id} style={{ marginBottom: '18px' }}>
              {/* La plata que el motor está descartando.
                  Un abono con fecha anterior al préstamo no cae en ningún
                  periodo, así que no se cuenta —y el cliente aparece debiendo
                  algo que sí pagó—. El programa no puede adivinar si la fecha
                  mala es la del abono o la del préstamo, pero sí puede dejar de
                  callárselo: va arriba del todo, antes que la cifra, porque
                  mientras esto exista la cifra está mal. */}
              {(() => {
                const rev = abonosFueraDePlazo(prestamo, pagos);
                if (!rev) return null;
                return (
                  <div style={{
                    background: '#fffbeb', border: '1px solid #fde68a',
                    borderLeft: '4px solid #f59e0b', borderRadius: '8px',
                    padding: '12px 14px', marginBottom: '14px',
                    color: '#92400e', fontSize: '14px',
                  }}>
                    <strong>⚠️ Ojo con las fechas de este préstamo</strong>
                    <div style={{ marginTop: '4px' }}>{avisoDeFechas(rev)}</div>
                    {rev.antes.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '13px' }}>
                        {rev.antes.map((p, j) => (
                          <div key={j}>
                            · {moneda(p.amount)} el {formatearFecha(p.payment_date)}
                            {' — el préstamo empieza el '}{formatearFecha(prestamo.start_date)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                Préstamo de {moneda(prestamo.amount)} al {prestamo.interest_rate}%
                {estados.length > 1 && <span style={{ color: '#9ca3af', fontWeight: 'normal' }}>({i + 1} de {estados.length})</span>}
                {prestamo.status !== 'active' && (
                  <span style={{
                    padding: '3px 10px', borderRadius: '12px', fontSize: '12px',
                    background: prestamo.status === 'pagado' ? '#dcfce7' : '#e0e7ff',
                    color: prestamo.status === 'pagado' ? '#166534' : '#3730a3'
                  }}>
                    {prestamo.status === 'pagado' ? '✅ Pagado' : '🤝 Cerrado por acuerdo'}
                  </span>
                )}
              </h3>
              <p style={{ color: '#6b7280', fontSize: '13px', marginTop: 0 }}>
                Desde el {formatearFecha(prestamo.start_date)}
                {estado.modalidad === 'cuota_fija'
                  ? <> · <strong>cuota fija</strong> de {moneda(estado.cuota)}</>
                  : prestamo.maturity_date
                    ? <> · vence el {formatearFecha(prestamo.maturity_date)}</>
                    : <> · <strong>abierto</strong>, sin fecha de vencimiento</>}
                {' · '}se cobra {({ daily: 'a diario', weekly: 'cada semana', biweekly: 'cada quincena' })[prestamo.frequency] || 'cada mes'}
              </p>

              {/* El cobradiario no cuenta la deuda en plata pendiente sino en
                  cuotas: «va por la nueve de treinta y lleva tres atrás». Es
                  como lo lleva en la cabeza quien cobra, y es lo que le dice al
                  cliente en la puerta. */}
              {estado.modalidad === 'cuota_fija' && (
                <div style={{
                  display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'baseline',
                  background: estado.cuotasAtrasadas > 0 ? '#fef2f2' : '#f0fdf4',
                  border: `1px solid ${estado.cuotasAtrasadas > 0 ? '#fecaca' : '#bbf7d0'}`,
                  borderRadius: '8px', padding: '12px 14px', marginBottom: '14px',
                  fontSize: '14px',
                }}>
                  <span>
                    Debe en total <strong>{moneda(estado.totalPactado)}</strong>
                    <span style={{ color: '#6b7280' }}> desde el primer día</span>
                  </span>
                  <span>
                    Va por la cuota <strong>{estado.cuotasPagadas}</strong> de{' '}
                    <strong>{estado.cuotasTotales}</strong>
                  </span>
                  {estado.cuotasAtrasadas > 0 ? (
                    <span style={{ color: '#b91c1c', fontWeight: 700 }}>
                      {estado.cuotasAtrasadas === 1
                        ? '1 cuota atrasada'
                        : `${estado.cuotasAtrasadas} cuotas atrasadas`}
                      {' '}({moneda(estado.atrasoEnPlata)})
                    </span>
                  ) : (
                    <span style={{ color: '#15803d', fontWeight: 700 }}>Al día</span>
                  )}
                </div>
              )}

              {/* «Van 14 días de los 30, ¿cuánto le cobro si quiere cancelar?»
                  El interés corre por meses y antes del corte no se debe —eso
                  no se toca—. Pero al cliente que aparece a mitad de mes
                  queriendo cancelar hay que decirle algo, y lo que hace de
                  cabeza quien presta es una regla de tres.

                  Es una SUGERENCIA para negociar, no la deuda. Por eso vive
                  aquí, en la pantalla que solo ve el prestamista, y no en el
                  estado de cuenta que recibe el cliente: dos cifras distintas
                  de «si paga hoy» en el mismo mensaje es una discusión
                  servida. */}
              {(() => {
                const est = estimadoPorDias(prestamo, estado);
                if (!est) return null;
                return (
                  <div style={{
                    background: '#eff6ff', border: '1px solid #bfdbfe',
                    borderRadius: '8px', padding: '12px 14px', marginBottom: '14px',
                    fontSize: '14px', color: '#1e40af',
                  }}>
                    <strong>Si quiere cancelar hoy</strong>
                    <div style={{ marginTop: '5px' }}>
                      Sin cobrarle los días: <strong>{moneda(est.sinCobrarDias)}</strong>
                    </div>
                    <div>
                      Cobrándole los {est.dias} días corridos de {est.diasDelPeriodo}:{' '}
                      <strong>{moneda(est.aPagar)}</strong>
                      <span style={{ color: '#4b5563' }}>
                        {' '}({moneda(est.interes)} de interés)
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '6px' }}>
                      Es una sugerencia para negociar. La cuenta del programa y lo
                      que ve el cliente siguen siendo {moneda(est.sinCobrarDias)}.
                    </div>
                  </div>
                );
              })()}

              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(135px, 1fr))',
                gap: '14px', background: '#f9fafb', border: '1px solid #e5e7eb',
                borderRadius: '8px', padding: '15px', marginBottom: '14px'
              }}>
                <Bloque
                  etiqueta="Capital pendiente"
                  valor={moneda(estado.capitalPendiente)}
                  color={estado.capitalPendiente > 0 ? '#991b1b' : '#166534'}
                  pie={estado.abonadoACapital > 0 ? `abonó ${moneda(estado.abonadoACapital)}` : null}
                />
                <Bloque
                  etiqueta="Interés generado"
                  valor={moneda(estado.interesGenerado)}
                  pie={`${estado.periodosGenerados} ${estado.periodosGenerados === 1 ? 'cobro' : 'cobros'}`}
                />
                <Bloque
                  etiqueta="Interés pagado"
                  valor={moneda(estado.interesPagado)}
                  color="#166534"
                />
                <Bloque
                  etiqueta={estado.interesPendiente >= 0 ? 'Interés que debe' : 'Interés a favor'}
                  valor={moneda(Math.abs(estado.interesPendiente))}
                  color={estado.interesPendiente > 0 ? '#b45309' : '#166534'}
                />
              </div>

              {prestamo.status !== 'active' && (
                <div style={{
                  background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px',
                  padding: '10px 12px', marginBottom: '14px', fontSize: '13px', color: '#374151'
                }}>
                  Cerrado el {formatearFecha(prestamo.cerrado_en)}.
                  {prestamo.condonado > 0 && (
                    <> Se condonaron <strong>{moneda(prestamo.condonado)}</strong>.</>
                  )}
                  {' '}Ya no genera intereses ni aparece en Morosos.
                </div>
              )}

              {prestamo.status === 'active' && (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
                  <button
                    type="button"
                    onClick={() => abrirCierre(prestamo, estado)}
                    style={{
                      padding: '9px 16px', borderRadius: '7px', border: '1px solid #c7d2fe',
                      background: '#eef2ff', color: '#4338ca', cursor: 'pointer',
                      fontSize: '14px', fontWeight: 'bold'
                    }}
                  >
                    🤝 Cerrar este préstamo
                  </button>
                  <button
                    type="button"
                    onClick={() => abrirRelleno(prestamo)}
                    style={{
                      padding: '9px 16px', borderRadius: '7px', border: '1px solid #e5e7eb',
                      background: 'white', color: '#374151', cursor: 'pointer', fontSize: '14px'
                    }}
                  >
                    📅 Cargar historial de pagos
                  </button>
                </div>
              )}

              {estado.aFavor > 0 && (
                <div style={{
                  background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px',
                  padding: '10px 12px', marginBottom: '14px', color: '#166534', fontSize: '13px'
                }}>
                  Va adelantado: pagó {moneda(estado.aFavor)} de interés que todavía no se ha generado.
                </div>
              )}

              {pagos.length === 0 ? (
                <p className="empty-state">Sin abonos todavía</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="payments-table" style={{ minWidth: '26rem' }}>
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Monto</th>
                        <th>Concepto</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <FilasDelHistorial
                        prestamo={prestamo} estado={estado}
                        pagos={pagos} cliente={cliente} />
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}

          {rellenando && (() => {
            const cobros = cobrosAGenerar(rellenando, hastaFecha);
            const monto = Math.round(rellenando.amount * (rellenando.interest_rate / 100));
            const yaTiene = (pagosPorPrestamo[rellenando.id] || []).length;

            return (
              <div
                onClick={() => !guardando && setRellenando(null)}
                style={{
                  position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                  padding: '40px 15px', zIndex: 100, overflowY: 'auto'
                }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: 'white', borderRadius: '12px', padding: '25px',
                    width: '100%', maxWidth: '470px', boxShadow: '0 10px 40px rgba(0,0,0,.25)'
                  }}
                >
                  <h3 style={{ marginTop: 0 }}>📅 Cargar historial de pagos</h3>
                  <p style={{ color: '#6b7280', fontSize: '13px', marginTop: 0 }}>
                    Para clientes que ya venían pagando antes de usar el programa.
                    Se anota un abono de interés por cada periodo transcurrido.
                  </p>

                  <div className="form-group">
                    <label>Estuvo pagando hasta</label>
                    <input
                      type="date"
                      value={hastaFecha}
                      onChange={(e) => setHastaFecha(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div style={{
                    background: '#f9fafb', border: '1px solid #e5e7eb',
                    borderRadius: '8px', padding: '14px', fontSize: '14px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: '#6b7280' }}>Desde</span>
                      <strong>{formatearFecha(rellenando.start_date)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: '#6b7280' }}>Abonos que se crearán</span>
                      <strong>{cobros}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: '#6b7280' }}>Cada uno de</span>
                      <strong>{moneda(monto)}</strong>
                    </div>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      paddingTop: '8px', borderTop: '1px solid #e5e7eb'
                    }}>
                      <span style={{ color: '#6b7280' }}>Total del historial</span>
                      <strong style={{ color: '#166534' }}>{moneda(monto * cobros)}</strong>
                    </div>
                  </div>

                  {yaTiene > 0 && (
                    <div style={{
                      background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px',
                      padding: '11px 13px', marginTop: '12px', fontSize: '13px', color: '#92400e'
                    }}>
                      Este préstamo ya tiene <strong>{yaTiene}</strong>{' '}
                      {yaTiene === 1 ? 'abono anotado' : 'abonos anotados'}. Los
                      nuevos se suman a esos, no los reemplazan — revisa que no
                      quedes con el mismo cobro dos veces.
                    </div>
                  )}

                  <div style={{
                    background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px',
                    padding: '11px 13px', marginTop: '12px', fontSize: '13px', color: '#1e40af'
                  }}>
                    Quedarán marcados como <strong>cargados del historial</strong>,
                    para distinguirlos de los que se anotan el día del cobro. Si
                    alguno no corresponde, se borra con la papelera.
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <button
                      type="button"
                      onClick={() => setRellenando(null)}
                      disabled={guardando}
                      style={{
                        flex: 1, padding: '12px', borderRadius: '6px', cursor: 'pointer',
                        border: '1px solid #e5e7eb', background: 'white', color: '#374151'
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={confirmarRelleno}
                      disabled={guardando || cobros === 0}
                      style={{
                        flex: 1, padding: '12px', borderRadius: '6px', border: 'none',
                        background: '#667eea', color: 'white', fontWeight: 'bold',
                        cursor: guardando || cobros === 0 ? 'not-allowed' : 'pointer',
                        opacity: guardando || cobros === 0 ? 0.7 : 1
                      }}
                    >
                      {guardando ? 'Cargando...' : `Crear ${cobros} abonos`}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {cerrando && (() => {
            const monto = parseFloat(recibido);
            const valido = Number.isFinite(monto) && monto >= 0;
            // La MISMA función que guarda. Si la vista previa hiciera su propia
            // cuenta, un día mostraría una cosa y guardaría otra — y en un
            // cierre de préstamo eso no se nota hasta cuadrar el mes.
            const r = repartirCierre(valido ? monto : 0, cerrando.estado)
                      || { interes: 0, capital: 0, condonado: 0, deMas: 0 };
            const { interes, capital, condonado } = r;

            return (
              <div
                onClick={() => !guardando && setCerrando(null)}
                style={{
                  position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                  padding: '40px 15px', zIndex: 100, overflowY: 'auto'
                }}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: 'white', borderRadius: '12px', padding: '25px',
                    width: '100%', maxWidth: '460px', boxShadow: '0 10px 40px rgba(0,0,0,.25)'
                  }}
                >
                  <h3 style={{ marginTop: 0 }}>🤝 Cerrar el préstamo</h3>
                  <p style={{ color: '#6b7280', fontSize: '13px', marginTop: 0 }}>
                    {cliente.name} · préstamo de {moneda(cerrando.prestamo.amount)}
                  </p>

                  <div className="form-group">
                    <label>¿Cuánto recibes para cerrar?</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={recibido}
                      onChange={(e) => setRecibido(e.target.value)}
                      autoFocus
                    />
                  </div>

                  {/* Las cuentas antes de confirmar: cerrar un préstamo no se
                      deshace, así que tiene que verse exactamente qué se guarda. */}
                  <div style={{
                    background: '#f9fafb', border: '1px solid #e5e7eb',
                    borderRadius: '8px', padding: '14px', fontSize: '14px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: '#6b7280' }}>Debe hoy</span>
                      <strong>{moneda(cerrando.estado.deudaTotal)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ color: '#6b7280' }}>Recibes</span>
                      <strong style={{ color: '#166534' }}>{moneda(r.recibido)}</strong>
                    </div>
                    {/* Lo que se recibe DE MÁS sobre lo calculado. Se dice antes
                        de guardar porque es justo lo que antes se perdía en
                        silencio: el mes completo cobrado a los pocos días. */}
                    {r.deMas > 0 && (
                      <div style={{ background: '#fffbeb', border: '1px solid #fcd34d',
                                    borderRadius: '6px', padding: '9px 11px',
                                    margin: '8px 0', fontSize: '13px', color: '#92400e' }}>
                        Son <strong>{moneda(r.deMas)}</strong> más de lo que el
                        programa tenía calculado. Se registran como{' '}
                        <strong>interés</strong>, que es lo que son cuando se
                        cobra el mes completo y el cliente devuelve antes.
                      </div>
                    )}
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      paddingTop: '8px', borderTop: '1px solid #e5e7eb'
                    }}>
                      <span style={{ color: '#6b7280' }}>
                        {condonado > 0 ? 'Condonas' : 'Queda a paz y salvo'}
                      </span>
                      <strong style={{ color: condonado > 0 ? '#b45309' : '#166534' }}>
                        {condonado > 0 ? moneda(condonado) : '✅'}
                      </strong>
                    </div>
                    <div style={{ color: '#9ca3af', fontSize: '12px', marginTop: '10px' }}>
                      Se registra como {moneda(interes)} de interés y {moneda(capital)} de capital.
                    </div>
                  </div>

                  {condonado > 0 && (
                    <div style={{
                      background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px',
                      padding: '11px 13px', marginTop: '12px', fontSize: '13px', color: '#92400e'
                    }}>
                      Los <strong>{moneda(condonado)}</strong> quedan registrados como
                      condonados. No se borran: es plata que dejaste de recibir y
                      conviene que se vea.
                    </div>
                  )}

                  <div style={{
                    background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px',
                    padding: '11px 13px', marginTop: '12px', fontSize: '13px', color: '#166534'
                  }}>
                    Al cerrarlo deja de generar intereses y sale de Morosos y de
                    Vencimientos. <strong>Esto no se puede deshacer.</strong>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <button
                      type="button"
                      onClick={() => setCerrando(null)}
                      disabled={guardando}
                      style={{
                        flex: 1, padding: '12px', borderRadius: '6px', cursor: 'pointer',
                        border: '1px solid #e5e7eb', background: 'white', color: '#374151'
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={confirmarCierre}
                      disabled={guardando || !valido}
                      style={{
                        flex: 1, padding: '12px', borderRadius: '6px', border: 'none',
                        background: '#667eea', color: 'white', fontWeight: 'bold',
                        cursor: guardando || !valido ? 'not-allowed' : 'pointer',
                        opacity: guardando || !valido ? 0.7 : 1
                      }}
                    >
                      {guardando ? 'Cerrando...' : '🤝 Cerrar préstamo'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="info-box">
            <h3>Cómo se calcula</h3>
            <p>
              El interés se genera cada periodo sobre el <strong>capital que
              esté vivo en ese momento</strong>. Si el cliente abona a capital,
              los periodos siguientes generan menos — pero los anteriores se
              quedan como estaban, porque en su momento sí debía todo.
            </p>
            <p>
              Los abonos parciales se acumulan: si el interés del mes son
              100.000 y da 50.000 dos veces, el mes queda cubierto. Si solo da
              una vez, quedan 50.000 pendientes y se arrastran al siguiente.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default DeudaCliente;
